// @/editorjs/tools/markdown/index.js

import EditorJS from '@editorjs/editorjs';
import Swal from 'sweetalert2';
import { convertMarkdownToBlocks } from '@/editorjs/markdown_paste_handler';
import {stopPropagationOnEditorJsControlKeys} from "@/editorjs/utils.js";

// SweetAlert를 위한 Tailwind CSS 믹스인 (실제 프로젝트 경로에 맞게 조정)
const swalTailwind = Swal.mixin({
    customClass: {
        popup: "py-12 bg-white dark:bg-stone-800",
        title: "text-2xl font-bold text-black dark:text-white",
        htmlContainer: "text-base py-16",
        icon: "border-0 mt-0",
        confirmButton: "maily-red-button mr-3",
        cancelButton: "maily-white-button"
    },
    buttonsStyling: false
});

export default class MarkdownTool {
    static get toolbox() {
        return {
            title: 'Markdown',
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" /><path d="M7 15v-6l2 2l2 -2v6" /><path d="M14 13l2 2l2 -2m-2 2v-6" /></svg>`
        };
    }

    constructor({ api, data, config, block }) {
        this.api = api;
        this.block = block;
        // 1. 데이터 구조 변경: markdown과 blocks를 모두 저장
        this.data = {
            markdown: data.markdown || '',
            blocks: data.blocks || [],
        };
        this.config = config; // 메인 에디터의 설정을 전달받기 위함
        this.lastParsedMarkdown = data.markdown || null;

        this.nodes = { wrapper: null, textarea: null, preview: null, previewToggleButton: null };
        this.previewEditor = null; // 미리보기용 Editor.js 인스턴스
        this.debounceTimer = null;
        this.isEditing = false; // 편집 모드 상태 플래그
        this.isComposing = false;
    }

    render() {
        this.nodes.wrapper = document.createElement('div');
        this.nodes.wrapper.classList.add('markdown-tool');

        const layout = document.createElement('div');
        layout.classList.add('markdown-tool__layout');

        const editorPane = document.createElement('div');
        editorPane.classList.add('markdown-tool__editor-pane');

        this.nodes.textarea = document.createElement('textarea');
        this.nodes.textarea.classList.add('ce-code__textarea');
        this.nodes.textarea.placeholder = '여기에 마크다운을 입력하세요...';
        this.nodes.textarea.value = this.data.markdown;

        const actionContainer = document.createElement('div');
        actionContainer.classList.add('markdown-tool__action-container');

        const convertButton = document.createElement('button');
        convertButton.type = 'button';
        convertButton.textContent = 'Markdown 내용을 블록으로 변환하기';
        convertButton.classList.add('maily-white-button');

        this.nodes.previewToggleButton = document.createElement('button');
        this.nodes.previewToggleButton.type = 'button';
        this.nodes.previewToggleButton.textContent = '미리보기';
        this.nodes.previewToggleButton.classList.add('maily-light-yellow-button'); // 다른 버튼과 간격

        editorPane.appendChild(this.nodes.textarea);

        // 2. 미리보기 창: Editor.js가 마운트될 div
        this.nodes.preview = document.createElement('div');
        // 고유한 ID를 부여하여 여러 MarkdownTool이 있어도 충돌하지 않도록 함
        this.nodes.preview.id = `markdown-preview-${this.block.id}`;
        this.nodes.preview.classList.add('markdown-tool__preview-pane', 'hidden');

        layout.appendChild(editorPane);
        layout.appendChild(this.nodes.preview);
        this.nodes.wrapper.appendChild(layout);
        actionContainer.appendChild(convertButton);
        actionContainer.appendChild(this.nodes.previewToggleButton);

        this.nodes.wrapper.appendChild(actionContainer);

        this._attachEventListeners(convertButton);
        this._updatePreview();

        return this.nodes.wrapper;
    }

    _attachEventListeners(button) {
        this.nodes.textarea.addEventListener('compositionstart', () => { this.isComposing = true; });
        this.nodes.textarea.addEventListener('compositionend', () => {
            this.isComposing = false;
            this._triggerPreviewUpdate();
        });
        this.nodes.textarea.addEventListener('compositionupdate', () => {
            this._triggerPreviewUpdate();
        });

        this.nodes.textarea.addEventListener('keydown', (event) => {
            stopPropagationOnEditorJsControlKeys(event);

            if (event.key === 'Tab' && !this.isComposing) {
                event.preventDefault(); // 기본 동작(포커스 이동) 방지

                const textarea = this.nodes.textarea;
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;

                // 커서 위치에 탭 문자 삽입
                textarea.value = textarea.value.substring(0, start) + '\t' + textarea.value.substring(end);
                // 커서 위치를 탭 문자 뒤로 이동
                textarea.selectionStart = textarea.selectionEnd = start + 1;
                this._triggerPreviewUpdate();
            }
        })

        this.nodes.textarea.addEventListener('input', () => {
            this.data.markdown = this.nodes.textarea.value; // 입력 시 마다 원본 markdown 데이터 업데이트
            this._triggerPreviewUpdate(); // 중복 로직을 새 헬퍼 메소드로 추출
        });

        this.nodes.textarea.addEventListener('scroll', () => this._syncScroll());
        this.nodes.preview.addEventListener('scroll', () => this._syncPreviewScroll());

        this.nodes.previewToggleButton.addEventListener('click', () => this._togglePreview());
        button.addEventListener('click', () => this.processConversion());
    }

    _triggerPreviewUpdate() {
        this.data.markdown = this.nodes.textarea.value;
        clearTimeout(this.debounceTimer);
        // 미리보기가 활성화 되어 있을 때만 업데이트 실행
        if (this.isEditing) {
            this.debounceTimer = setTimeout(() => this._updatePreview(), 200);
        }
    }

    async _togglePreview() {
        // isEditing이 false (미리보기가 숨겨진 상태)이면 보이게 처리
        if (!this.isEditing) {
            this.isEditing = true;
            this.nodes.previewToggleButton.textContent = '미리보기 닫기';
            this.nodes.preview.classList.remove('hidden');

            // 미리보기 영역 높이를 textarea와 동일하게 설정
            this.nodes.preview.style.height = `${this.nodes.textarea.offsetHeight}px`;

            // 미리보기 에디터가 없으면 최초 생성
            if (!this.previewEditor) {
                this.previewEditor = new EditorJS({
                    holder: this.nodes.preview.id,
                    readOnly: true,
                    tools: this.config.tools,
                    data: { blocks: this.data.blocks },
                });
                await this.previewEditor.isReady;
            }

            // 표시하는 시점에 최신 내용으로 미리보기 업데이트
            await this._updatePreview();

        } else { // isEditing이 true (미리보기가 보이는 상태)이면 숨김 처리
            this.isEditing = false;
            this.nodes.previewToggleButton.textContent = '미리보기';
            this.nodes.preview.classList.add('hidden');
        }
    }

    // 2-1. 미리보기 Editor.js 생성 및 업데이트
    async _updatePreview() {
        if (this.data.markdown === this.lastParsedMarkdown) {
            this._syncScroll(); // 스크롤만 동기화하고 종료
            return;
        }

        this.data.blocks = convertMarkdownToBlocks(this.data.markdown);
        this.lastParsedMarkdown = this.data.markdown; // 마지막 파싱 내용 업데이트

        if (!this.previewEditor || !this.previewEditor.isReady) return;

        try {
            await this.previewEditor.isReady;
            await this.previewEditor.render({ blocks: this.data.blocks });
            this._syncScroll();
        } catch (e) {
            console.error("Preview editor update failed:", e);
        }
    }

    _syncScroll() {
        if (!this.previewEditor || !this.isEditing) return; // 미리보기가 활성화 상태일 때만 동기화

        const textarea = this.nodes.textarea;
        // 미리보기 패널의 실제 스크롤 가능한 영역을 찾음
        const previewScroller = this.nodes.preview;
        if (!previewScroller) return;

        const scrollRatio = textarea.scrollTop / (textarea.scrollHeight - textarea.clientHeight);
        previewScroller.scrollTop = scrollRatio * (previewScroller.scrollHeight - previewScroller.clientHeight);
    }

    _syncPreviewScroll() {
        if (!this.previewEditor || !this.isEditing) return; // 미리보기가 활성화 상태일 때만 동기화

        const preview = this.nodes.preview;
        // 미리보기 패널의 실제 스크롤 가능한 영역을 찾음
        const textareaScroller = this.nodes.textarea;
        if (!textareaScroller) return;

        const scrollRatio = preview.scrollTop / (preview.scrollHeight - preview.clientHeight);
        textareaScroller.scrollTop = scrollRatio * (textareaScroller.scrollHeight - textareaScroller.clientHeight);
    }

    // 3. SweetAlert를 사용한 변환 확인 로직
    async processConversion() {
        if (!this.data.markdown.trim()) {
            this.api.notifier.show({ message: "마크다운 내용을 먼저 입력해주세요.", style: 'info' });
            return;
        }

        const currentBlockId = this.block.id;
        const currentBlockIndex = this.api.blocks.getBlockIndex(currentBlockId);

        if (currentBlockIndex < 0) {
            console.error("MarkdownTool: Could not find the block's own index.");
            return;
        }

        const result = await swalTailwind.fire({
            title: "정말 블록으로 변환하시겠습니까?",
            text: "이 작업은 되돌릴 수 없으며, 현재 입력된 마크다운 내용은 사라집니다.",
            iconHtml: "⚠️",
            showCancelButton: true,
            cancelButtonText: "취소",
            confirmButtonText: "네, 변환합니다",
        });

        if (!result.isConfirmed) {
            return;
        }

        const blocksToInsert = this.data.blocks.length > 0 ? this.data.blocks : convertMarkdownToBlocks(this.data.markdown);

        if (blocksToInsert.length === 0) {
            this.api.notifier.show({ message: "변환할 유효한 블록이 없습니다.", style: 'info' });
            return;
        }

        try {
            let lastInsertedBlockId = currentBlockId;
            for (const blockData of blocksToInsert) {
                // insert는 삽입된 블록의 인스턴스를 반환합니다.
                const newBlock = this.api.blocks.insert(
                    blockData.type,
                    blockData.data,
                    {}, // config
                    this.api.blocks.getBlockIndex(lastInsertedBlockId) + 1, // 바로 다음 위치
                    false // focus
                );
                lastInsertedBlockId = newBlock.id; // 다음 삽입 위치를 위해 ID 업데이트
            }

            await this.api.blocks.delete(currentBlockIndex);

            // 5. 모든 블록 조작이 끝난 후, 안정화를 위해 짧은 지연 뒤에 포커스를 설정합니다.
            setTimeout(() => {
                this.api.caret.setToBlock(this.api.blocks.getById(currentBlockId), 'start');
            }, 100);

            this.api.notifier.show({
                message: '마크다운이 블록으로 변환되었습니다.',
                style: 'success',
            });

        } catch (e) {
            console.error("Error during block conversion:", e);
            this.api.notifier.show({
                message: "블록 변환 중 오류가 발생했습니다.",
                style: 'error'
            });
        }
    }

    // 1-1. 저장 데이터 구조 변경
    save() {
        if (this.data.markdown !== this.lastParsedMarkdown) {
            this.data.blocks = convertMarkdownToBlocks(this.data.markdown);
        }

        return this.data;
    }

    destroy() {
        clearTimeout(this.debounceTimer);
        if (this.previewEditor && typeof this.previewEditor.destroy === 'function') {
            this.previewEditor.destroy();
        }
    }
}